import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * <ambera-reviews> — progressive enhancement for the Judge.me review widget
 * (sections/product-reviews.liquid). The widget's markup belongs to Judge.me: it arrives
 * server-side from the `judgeme.widget` product metafield and is then re-rendered by
 * Judge.me's own script on sort and pagination. This element never restructures it — it only
 * adds the pieces the design asks for that the widget does not ship:
 *
 *   1. the section heading, so the merchant sets it in the theme editor instead of Judge.me;
 *   2. the large average score (Judge.me prints "4.75 out of 5" inside the stars);
 *   3. the filter bar (Figma 758:7233): "Filtre" with the "Reviews 1–5 of 51" counter, then a
 *      rating select, "with photos" and "verified only" toggles and a sort select;
 *   4. "Vezi mai multe recenzii" (load more) instead of Judge.me's pager.
 *      Judge.me's widget API has no verified-only filter, and its pager replaces the list (and
 *      scrolls the page) on every step, so the element fetches every review of the product once
 *      (pages of 30 from the same endpoint the widget uses; two requests for 51 reviews) and
 *      filters, sorts and reveals them five at a time itself. The fetched markup is Judge.me's
 *      own, finished the way its script would (relative dates, lazy photos). Until that
 *      download lands, the server-rendered first page stays as it is;
 *   5. the write-a-review form as a drawer on the right. Judge.me still owns the form: it shows
 *      and hides `.jdgm-form-wrapper` with an inline `display`, and its handlers are bound
 *      inside the widget, so the form stays where it is and the stylesheet pins it to the
 *      viewport. This element only adds the backdrop, a close button, Escape, focus and the
 *      scroll lock, and closes through Judge.me's own cancel link so its state stays in step.
 *
 * Everything is re-applied through a MutationObserver, because Judge.me replaces the review
 * list wholesale whenever the shopper sorts or pages. The observer is paused while we write,
 * so our own mutations never feed back into it.
 */
class AmberaReviews extends HTMLElement {
  /** @type {MutationObserver | null} */
  #observer = null;
  /** @type {HTMLElement | null} The open form panel, or null while the drawer is closed. */
  #drawer = null;
  /** @type {HTMLElement | null} */
  #backdrop = null;
  /** @type {Element | null} Where focus goes back to when the drawer closes. */
  #returnFocus = null;
  /** Until this time, a freshly built form is opened for the click that asked for it. */
  #openRequestedUntil = 0;
  /** @type {HTMLElement | null} The filter bar, cloned from the section's <template>. */
  #filters = null;
  /** @type {HTMLElement | null} */
  #more = null;
  /** @type {Element[] | null} Every review of the product, in Judge.me's order, once fetched. */
  #all = null;
  /** @type {Promise<void> | null} */
  #loading = null;
  /** @type {Element[]} What the list shows right now, in order. */
  #shown = [];
  #state = { rating: 0, photos: false, verified: false, sort: 'recent' };
  #perPage = 5;
  #visible = 5;

  connectedCallback() {
    this.#observer = new MutationObserver(() => this.#apply());
    this.addEventListener('click', this.#onClick, true);
    this.addEventListener('keydown', this.#onRowKey, true);
    this.#apply();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.removeEventListener('click', this.#onClick, true);
    this.removeEventListener('keydown', this.#onRowKey, true);
    if (this.#drawer) this.#setDrawer(null);
  }

  #apply() {
    const observer = this.#observer;
    if (!observer) return;

    // Pause while writing and drop the records our own writes queued. A flag released on a later
    // frame would also swallow Judge.me's re-render when it lands before that frame.
    observer.disconnect();
    try {
      const widget = this.querySelector('.jdgm-rev-widg');
      if (widget) {
        this.#applyHeading(widget);
        this.#applyWriteLabel(widget);
        this.#applyScore(widget);
        this.#applyFilters(widget);
        this.#applyDrawer(widget);
      }
    } finally {
      observer.takeRecords();
      // `style` is how Judge.me opens and closes the form.
      observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
    }
  }

  /** @param {Element} widget */
  #applyHeading(widget) {
    const heading = this.dataset.heading?.trim();
    if (!heading) return;

    const title = widget.querySelector('.jdgm-rev-widg__title');
    if (title && title.textContent.trim() !== heading) title.textContent = heading;
  }

  /**
   * Judge.me writes its button text in the Judge.me language (English here) whatever the store
   * language; the section setting carries the translated label.
   * @param {Element} widget
   */
  #applyWriteLabel(widget) {
    const label = this.dataset.writeLabel?.trim();
    if (!label) return;

    const link = widget.querySelector('.jdgm-write-rev-link');
    if (link && link.textContent.trim() !== label) link.textContent = label;
  }

  /** @param {HTMLElement} widget */
  #applyScore(widget) {
    const summary = widget.querySelector('.jdgm-rev-widg__summary');
    const average = Number(widget.dataset.averageRating);
    if (!summary || !Number.isFinite(average) || average <= 0) return;

    let score = summary.querySelector(':scope > .ambera-reviews__score');
    if (!score) {
      score = document.createElement('span');
      score.className = 'ambera-reviews__score';
      // The same number is already announced by the stars' aria-label.
      score.setAttribute('aria-hidden', 'true');
      summary.prepend(score);
    }

    const text = average.toFixed(1);
    if (score.textContent !== text) score.textContent = text;
  }

  /** @param {HTMLElement} widget */
  #applyFilters(widget) {
    const body = widget.querySelector('.jdgm-rev-widg__body');
    const total = Number(widget.dataset.numberOfReviews) || 0;
    if (!body || total <= 0) return;

    if (!this.#filters) {
      const template = this.querySelector('template[data-reviews-filters]');
      if (!(template instanceof HTMLTemplateElement)) return;
      this.#filters = /** @type {HTMLElement} */ (template.content.firstElementChild?.cloneNode(true));
      this.#filters.addEventListener('change', this.#onFilterChange);
      this.#filters.addEventListener('click', this.#onFilterClick);

      this.#more = document.createElement('div');
      this.#more.className = 'ambera-reviews__more';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ambera-reviews__more-button';
      button.textContent = this.dataset.loadMore || 'Vezi mai multe recenzii';
      button.addEventListener('click', this.#onMore);
      this.#more.append(button);

      const perPage = Number(widget.querySelector('.jdgm-paginate')?.dataset.perPage);
      if (perPage > 0) this.#perPage = this.#visible = perPage;
    }
    if (this.#filters.nextElementSibling !== body) body.before(this.#filters);
    if (this.#more && this.#more.parentElement !== this) this.append(this.#more);

    if (!this.#all && !this.#loading) this.#loading = this.#fetchAll(widget);

    // Judge.me re-rendered the list on its own (its script hydrating late, say): show ours again.
    const list = widget.querySelector('.jdgm-rev-widg__reviews');
    if (this.#all && list) {
      const onScreen = [...list.querySelectorAll(':scope > .jdgm-rev')];
      if (onScreen.length !== this.#shown.length || onScreen.some((review, i) => review !== this.#shown[i])) {
        this.#render(widget, false);
      }
    }

    this.#updateCounter(widget);
  }

  /** @param {HTMLElement} widget */
  async #fetchAll(widget) {
    const productId = this.querySelector('#judgeme_product_reviews')?.getAttribute('data-id');
    const shop = window.Shopify?.shop;
    const endpoint = widget.querySelector('.jdgm-paginate')?.getAttribute('data-url') || 'https://api.judge.me/reviews/reviews_for_widget';
    if (!productId || !shop) return;

    this.#filters?.setAttribute('aria-busy', 'true');
    try {
      /** @type {Element[]} */
      const reviews = [];
      for (let page = 1; page <= 20; page++) {
        const params = new URLSearchParams({ url: shop, shop_domain: shop, platform: 'shopify', page: String(page), per_page: '30', product_id: productId });
        const response = await fetch(`${endpoint}?${params}`);
        if (!response.ok) throw new Error(`Judge.me answered ${response.status}`);
        const data = await response.json();
        const doc = new DOMParser().parseFromString(data.html || '', 'text/html');
        const batch = [...doc.querySelectorAll('.jdgm-rev-widg__reviews > .jdgm-rev')];
        reviews.push(...batch.map((review) => this.#finish(document.importNode(review, true))));
        if (batch.length < 30 || reviews.length >= (Number(data.total_count) || 0)) break;
      }

      // Keep the server-rendered nodes for the reviews already on screen, so nothing flickers.
      const list = widget.querySelector('.jdgm-rev-widg__reviews');
      const existing = new Map(
        [...(list?.querySelectorAll(':scope > .jdgm-rev') ?? [])].map((review) => [review.getAttribute('data-review-id'), review])
      );
      this.#all = reviews.map((review) => existing.get(review.getAttribute('data-review-id')) ?? review);
      this.#all.forEach((review, index) => review.setAttribute('data-ambera-index', String(index)));
      this.#apply();
      this.#render(widget, false);
    } catch (error) {
      // Leave Judge.me's first page in place; the filters and "load more" just stay inert.
      console.warn('Reviews could not be loaded:', error);
      if (this.#more) this.#more.hidden = true;
    } finally {
      this.#filters?.removeAttribute('aria-busy');
    }
  }

  /**
   * What Judge.me's script does to review markup after it arrives: relative dates and photos.
   * @param {Element} review
   */
  #finish(review) {
    const time = review.querySelector('time.jdgm-rev__timestamp');
    if (time && !time.textContent.trim()) {
      const date = new Date(time.getAttribute('datetime') || '');
      if (!Number.isNaN(date.getTime())) time.textContent = relativeTime(date);
      time.classList.remove('jdgm-spinner');
    }
    for (const img of review.querySelectorAll('img[data-src]')) {
      img.setAttribute('src', img.getAttribute('data-src') || '');
      img.setAttribute('loading', 'lazy');
    }
    for (const link of review.querySelectorAll('.jdgm--loading')) link.classList.remove('jdgm--loading');
    return review;
  }

  /** @returns {Element[]} */
  #filtered() {
    const { rating, photos, verified, sort } = this.#state;
    const score = (/** @type {Element} */ review) => Number(review.querySelector('.jdgm-rev__rating')?.getAttribute('data-score')) || 0;
    const index = (/** @type {Element} */ review) => Number(review.getAttribute('data-ambera-index')) || 0;

    const reviews = (this.#all ?? []).filter(
      (review) =>
        (!rating || score(review) === rating) &&
        (!photos || review.querySelector('.jdgm-rev__pics img, .jdgm-rev__vids video, .jdgm-rev__vids iframe')) &&
        (!verified || review.getAttribute('data-verified-buyer') === 'true')
    );
    if (sort === 'highest') reviews.sort((a, b) => score(b) - score(a) || index(a) - index(b));
    if (sort === 'lowest') reviews.sort((a, b) => score(a) - score(b) || index(a) - index(b));
    return reviews;
  }

  /**
   * @param {HTMLElement} widget
   * @param {boolean} append  true: only add what "load more" revealed, leave the rest untouched
   */
  #render(widget, append) {
    const list = widget.querySelector('.jdgm-rev-widg__reviews');
    if (!list || !this.#all) return;

    const observer = this.#observer;
    observer?.disconnect();
    try {
      const reviews = this.#filtered();
      const next = reviews.slice(0, this.#visible);
      if (append) list.append(...next.slice(this.#shown.length));
      else list.replaceChildren(...next);
      this.#shown = next;

      let empty = widget.querySelector('.ambera-reviews__empty');
      if (!reviews.length) {
        if (!empty) {
          empty = document.createElement('p');
          empty.className = 'ambera-reviews__empty';
          empty.textContent = this.dataset.empty || '';
          list.after(empty);
        }
      } else {
        empty?.remove();
      }

      if (this.#more) this.#more.hidden = next.length >= reviews.length;
      this.#updateCounter(widget, reviews.length);
      this.#syncHistogram(widget);
    } finally {
      observer?.takeRecords();
      observer?.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
    }
  }

  /**
   * @param {HTMLElement} widget
   * @param {number} [total]
   */
  #updateCounter(widget, total) {
    const counter = this.#filters?.querySelector('.ambera-reviews__bar');
    if (!counter) return;
    const template = this.dataset.counter?.trim() || '';
    const count = total ?? (this.#all ? this.#filtered().length : Number(widget.dataset.numberOfReviews) || 0);
    const shown = this.#all ? this.#shown.length : widget.querySelectorAll('.jdgm-rev-widg__reviews > .jdgm-rev').length;

    const text =
      template && count > 0 && shown > 0
        ? template.replaceAll('{from}', '1').replaceAll('{to}', String(shown)).replaceAll('{total}', String(count))
        : '';
    if (counter.textContent !== text) counter.textContent = text;
  }

  /** The histogram rows filter by rating too; mark the active one. @param {HTMLElement} widget */
  #syncHistogram(widget) {
    for (const row of widget.querySelectorAll('.jdgm-histogram__row[data-rating]')) {
      const rating = Number(row.getAttribute('data-rating'));
      row.toggleAttribute('data-ambera-active', Boolean(this.#state.rating) && rating === this.#state.rating);
    }
    widget.querySelector('.jdgm-histogram')?.toggleAttribute('data-ambera-filtered', Boolean(this.#state.rating));
  }

  /** Set the state, mirror it in the controls, and show the first page of the result. */
  async #update(/** @type {Partial<typeof this.#state>} */ change) {
    Object.assign(this.#state, change);
    this.#syncControls();
    await this.#loading;
    const widget = this.querySelector('.jdgm-rev-widg');
    if (!(widget instanceof HTMLElement)) return;
    this.#visible = this.#perPage;
    this.#render(widget, false);
  }

  #syncControls() {
    const filters = this.#filters;
    if (!filters) return;
    for (const select of filters.querySelectorAll('select')) {
      const key = /** @type {'rating' | 'sort'} */ (select.name);
      select.value = String(this.#state[key]);
      const chip = select.closest('.ambera-reviews__chip');
      const text = chip?.querySelector('.ambera-reviews__chip-text');
      const isDefault = select.selectedIndex <= 0;
      chip?.toggleAttribute('data-active', !isDefault);
      if (text) text.textContent = isDefault ? text.getAttribute('data-default') || '' : select.selectedOptions[0]?.textContent?.trim() || '';
    }
    for (const toggle of filters.querySelectorAll('button[aria-pressed]')) {
      const key = /** @type {'photos' | 'verified'} */ (toggle.getAttribute('name'));
      toggle.setAttribute('aria-pressed', String(this.#state[key]));
    }
  }

  /** @param {Event} event */
  #onFilterChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    if (select.name === 'rating') this.#update({ rating: Number(select.value) || 0 });
    if (select.name === 'sort') this.#update({ sort: select.value });
  };

  /** @param {MouseEvent} event */
  #onFilterClick = (event) => {
    const toggle = event.target instanceof Element ? event.target.closest('button[aria-pressed]') : null;
    if (!toggle) return;
    const key = /** @type {'photos' | 'verified'} */ (toggle.getAttribute('name'));
    this.#update({ [key]: toggle.getAttribute('aria-pressed') !== 'true' });
  };

  #onMore = async () => {
    const button = this.#more?.querySelector('button');
    button?.setAttribute('aria-busy', 'true');
    await this.#loading;
    button?.removeAttribute('aria-busy');
    const widget = this.querySelector('.jdgm-rev-widg');
    if (!(widget instanceof HTMLElement) || !this.#all) return;
    this.#visible += this.#perPage;
    this.#render(widget, true);
  };

  /** @param {Element} widget */
  #applyDrawer(widget) {
    const panel = widget.querySelector('.jdgm-form-wrapper');
    if (!(panel instanceof HTMLElement)) return;

    if (!panel.querySelector(':scope > .ambera-reviews__drawer-close')) {
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      panel.tabIndex = -1;

      const title = panel.querySelector('.jdgm-form__title');
      if (title) {
        title.id ||= 'ambera-reviews-form-title';
        panel.setAttribute('aria-labelledby', title.id);
      }

      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'ambera-reviews__drawer-close';
      close.setAttribute('aria-label', this.dataset.closeLabel || 'Close');
      panel.prepend(close);
    }

    if (!this.#backdrop) {
      this.#backdrop = document.createElement('div');
      this.#backdrop.className = 'ambera-reviews__backdrop';
      this.#backdrop.hidden = true;
      this.append(this.#backdrop);
    }

    const open = getComputedStyle(panel).display !== 'none';

    // Judge.me builds the form on the first click after page load but leaves it closed, so the
    // shopper had to click twice. Replay that click once the form exists.
    if (!open && Date.now() < this.#openRequestedUntil) {
      this.#openRequestedUntil = 0;
      const link = widget.querySelector('.jdgm-write-rev-link');
      if (link instanceof HTMLElement) setTimeout(() => link.click());
    }

    if (open && this.#drawer !== panel) this.#setDrawer(panel);
    else if (!open && this.#drawer) this.#setDrawer(null);
  }

  /** @param {HTMLElement | null} panel */
  #setDrawer(panel) {
    const previous = this.#drawer;
    this.#drawer = panel;
    if (this.#backdrop) this.#backdrop.hidden = !panel;

    if (panel) {
      this.#returnFocus = document.activeElement;
      lockScroll(this);
      document.addEventListener('keydown', this.#onKeydown);
      panel.focus({ preventScroll: true });
      return;
    }

    unlockScroll(this);
    document.removeEventListener('keydown', this.#onKeydown);
    // Only take focus back if it was still inside the drawer (or dropped to <body> with it).
    const active = document.activeElement;
    if (!active || active === document.body || previous?.contains(active)) {
      if (this.#returnFocus instanceof HTMLElement) this.#returnFocus.focus({ preventScroll: true });
    }
    this.#returnFocus = null;
  }

  #closeDrawer() {
    const panel = this.#drawer;
    if (!panel) return;

    // Judge.me's own controls flip the write button's label and reset the form state.
    const cancel = panel.querySelector('.jdgm-cancel-rev') ?? this.querySelector('.jdgm-write-rev-link');
    if (cancel instanceof HTMLElement) cancel.click();
    // Fall back if Judge.me did not hide it (script missing or changed).
    if (getComputedStyle(panel).display !== 'none') panel.style.display = 'none';
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // A histogram row filters by that rating (clicking the active one clears it). Judge.me would
    // refetch its own list; this element owns the list now, so the click stops here.
    const row = target.closest('#judgeme_product_reviews .jdgm-histogram__row');
    if (row && this.#filters) {
      event.preventDefault();
      event.stopPropagation();
      const rating = Number(row.getAttribute('data-rating')) || 0;
      this.#update({ rating: rating === this.#state.rating ? 0 : rating });
      return;
    }

    if (target.closest('.jdgm-write-rev-link') && !this.querySelector('.jdgm-form-wrapper')) {
      this.#openRequestedUntil = Date.now() + 5000;
      return;
    }

    if (target === this.#backdrop || target.closest('.ambera-reviews__drawer-close')) this.#closeDrawer();
  };

  /** Enter or Space on a focused histogram row (Judge.me makes them focusable). @param {KeyboardEvent} event */
  #onRowKey = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const row = event.target instanceof Element ? event.target.closest('#judgeme_product_reviews .jdgm-histogram__row') : null;
    if (!(row instanceof HTMLElement)) return;
    event.preventDefault();
    event.stopPropagation();
    row.click();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const panel = this.#drawer;
    if (!panel) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.#closeDrawer();
      return;
    }

    if (event.key !== 'Tab') return;

    // Keep Tab inside the drawer.
    const focusable = [
      ...panel.querySelectorAll('a[href], button, input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'),
    ].filter((element) => element instanceof HTMLElement && !element.hasAttribute('disabled') && element.offsetParent);
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!(first instanceof HTMLElement) || !(last instanceof HTMLElement)) return;

    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !panel.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  };
}

const relativeFormat = new Intl.RelativeTimeFormat(document.documentElement.lang || 'ro', { numeric: 'always' });

/** "acum 2 săptămâni", as Judge.me prints review dates. @param {Date} date */
function relativeTime(date) {
  const days = Math.round((Date.now() - date.getTime()) / 86400000);
  if (days < 1) return relativeFormat.format(0, 'day');
  if (days < 7) return relativeFormat.format(-days, 'day');
  if (days < 30) return relativeFormat.format(-Math.floor(days / 7), 'week');
  if (days < 365) return relativeFormat.format(-Math.floor(days / 30), 'month');
  return relativeFormat.format(-Math.floor(days / 365), 'year');
}

if (!customElements.get('ambera-reviews')) {
  customElements.define('ambera-reviews', AmberaReviews);
}
